import {useMemo, FC, useState} from 'react';
import {
  Table,
  Header,
  Box,
  Input,
  Select,
  Checkbox,
  Button,
  Textarea,
  SpaceBetween,
  Modal,
  FormField,
  Container
} from '@amzn/awsui-components-react';
import { ToolParameterSchema } from 'src/store/interfaces/Tool.types';

// Proper type definitions to avoid any types
type PrimitiveValue = string | number | boolean;
type SimpleArray = PrimitiveValue[];
type NestedArray = (PrimitiveValue | SimpleArray)[];
type ParameterFieldValue = PrimitiveValue | SimpleArray | NestedArray | null | undefined;

type ExtendedToolParameterSchema = ToolParameterSchema & { internalName: string };

interface ToolParametersTableProps {
  parameters: ExtendedToolParameterSchema[];
  onParameterChange: (index: number, field: keyof ExtendedToolParameterSchema, value: PrimitiveValue | SimpleArray) => void;
  onRemoveParameter?: (index: number) => void;
  showActions?: boolean;
  requiredParameters?: string[];
  isEditable?: boolean;
}

// Configuration for fields that support nested/array editing
const FIELD_CONFIG = {
  default: { 
    displayName: 'Default Value', 
    supportsNested: true, 
    placeholder: 'default value',
    isArrayOnly: false
  },
  examples: { 
    displayName: 'Example Value', 
    supportsNested: true, 
    placeholder: 'example value',
    isArrayOnly: true
  },
  enum: { 
    displayName: 'Enum Values', 
    supportsNested: false, 
    placeholder: 'enum value',
    isArrayOnly: true
  }
} as const;

type ConfigurableField = keyof typeof FIELD_CONFIG;

const NestedListEditor: FC<{
  values: NestedArray;
  onChange: (newValues: NestedArray) => void;
  placeholder?: string;
  level?: number;
}> = ({ values, onChange, placeholder = "Enter value", level = 0 }) => {
  const addStringItem = () => {
    onChange([...values, '']);
  };

  const addListItem = () => {
    onChange([...values, []]);
  };

  const removeItem = (index: number) => {
    const newValues = values.filter((_, i) => i !== index);
    onChange(newValues);
  };

  const updateItem = (index: number, value: PrimitiveValue | SimpleArray | NestedArray) => {
    const newValues = [...values];
    newValues[index] = value as PrimitiveValue | SimpleArray;
    onChange(newValues);
  };

  const isItemList = (item: PrimitiveValue | SimpleArray): item is SimpleArray => Array.isArray(item);

  return (
    <Container>
      <SpaceBetween direction="vertical" size="xs">
        {values.map((value, index) => (
          <SpaceBetween key={index} direction="vertical" size="xs">
            <SpaceBetween direction="horizontal" size="xs">
              {isItemList(value) ? (
                <Box variant="code" fontSize="body-s" color="text-status-info">
                  List ({value.length} items)
                </Box>
              ) : (
                <Input
                  value={String(value)}
                  onChange={({ detail }) => {
                    updateItem(index, detail.value);
                  }}
                  placeholder={placeholder}
                />
              )}
              <Button
                variant="icon"
                iconName="remove"
                onClick={() => removeItem(index)}
              />
              {!isItemList(value) && (
                <Button
                  variant="icon"
                  iconName="folder"
                  onClick={() => updateItem(index, [])}
                  ariaLabel="Convert to list"
                />
              )}
            </SpaceBetween>
            
            {isItemList(value) && (
              <Container>
                <NestedListEditor
                  values={value as NestedArray}
                  onChange={(newNestedValues) => updateItem(index, newNestedValues)}
                  placeholder={placeholder}
                  level={level + 1}
                />
              </Container>
            )}
          </SpaceBetween>
        ))}
        
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            variant="icon"
            iconName="add-plus"
            onClick={addStringItem}
          >
            Add item
          </Button>
          <Button
            variant="icon"
            iconName="folder-open"
            onClick={addListItem}
          >
            Add list
          </Button>
        </SpaceBetween>
      </SpaceBetween>
    </Container>
  );
};

// Array editor component for simple arrays
const ArrayEditor: FC<{
  values: SimpleArray;
  onChange: (newValues: SimpleArray) => void;
  placeholder?: string;
}> = ({ values, onChange, placeholder = "Enter value" }) => {
  const addItem = () => {
    onChange([...values, '']);
  };

  const removeItem = (index: number) => {
    const newValues = values.filter((_, i) => i !== index);
    onChange(newValues);
  };

  const updateItem = (index: number, value: PrimitiveValue) => {
    const newValues = [...values];
    newValues[index] = value;
    // Ensure all values in the array are consistently parsed for numbers
    const consistentValues = newValues.map(val => {
      const strVal = String(val).trim();
      const numVal = Number(strVal);
      return (!isNaN(numVal) && strVal !== '' && isFinite(numVal)) ? numVal : val;
    });
    onChange(consistentValues);
  };

  return (
    <SpaceBetween direction="vertical" size="xs">
      {values.map((value, index) => (
        <SpaceBetween key={index} direction="horizontal" size="xs">
          <Input
            value={String(value)}
            onChange={({ detail }) => {
              updateItem(index, detail.value);
            }}
            placeholder={placeholder}
          />
          <Button
            variant="icon"
            iconName="remove"
            onClick={() => removeItem(index)}
          />
        </SpaceBetween>
      ))}
      <Button
        variant="icon"
        iconName="add-plus"
        onClick={addItem}
      >
        Add item
      </Button>
    </SpaceBetween>
  );
};

const ToolParametersTable: FC<ToolParametersTableProps> = ({
  parameters,
  onParameterChange,
  requiredParameters = [],
  isEditable = true
}) => {

  // Shared function to format display values consistently
  const formatDisplayValue = (value: ParameterFieldValue): string => {
    if (Array.isArray(value)) {
      return JSON.stringify(value).replace(/,/g, ', ');
    } else if (value !== undefined && value !== null) {
      if (typeof value === 'string') {
        return value === '' ? "" : `"${value}"`;
      } else {
        return String(value);
      }
    }
    return '-';
  };

  // Track nested mode state for each parameter+field combination
  const [nestedModeStates, setNestedModeStates] = useState<Record<string, boolean>>({});
  // Modal state for editing
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    parameterIndex: number | null;
    field: ConfigurableField | null;
    title: string;
    currentValue: ParameterFieldValue;
  }>({
    isOpen: false,
    parameterIndex: null,
    field: null,
    title: '',
    currentValue: null
  });
  // Temporary value for modal editing
  const [tempValue, setTempValue] = useState<ParameterFieldValue>(null);
  
  const isParameterInRequiredList = (internalName: string) => {
    return requiredParameters.includes(internalName);
  };

  const canEditRequiredField = (internalName: string) => {
    return requiredParameters.length === 0 || !isParameterInRequiredList(internalName);
  };

  // Helper functions for modal editing
  const openModal = (parameterIndex: number, field: ConfigurableField, currentValue: ParameterFieldValue) => {
    const parameter = parameters[parameterIndex];
    const fieldConfig = FIELD_CONFIG[field];
    
    setModalState({
      isOpen: true,
      parameterIndex,
      field,
      title: `Edit ${fieldConfig.displayName} for ${parameter.internalName}`,
      currentValue
    });
    setTempValue(currentValue || null);
    
    // Initialize nested mode state if field supports nested arrays
    if (fieldConfig.supportsNested) {
      const stateKey = `${parameterIndex}-${field}`;
      if (nestedModeStates[stateKey] === undefined) {
        const hasNestedArrays = Array.isArray(currentValue) && currentValue.some(val => Array.isArray(val));
        setNestedModeStates(prev => ({
          ...prev,
          [stateKey]: hasNestedArrays
        }));
      }
    }
  };
  
  const closeModal = () => {
    setModalState({
      isOpen: false,
      parameterIndex: null,
      field: null,
      title: '',
      currentValue: null
    });
    setTempValue(null);
  };
  
  const saveModal = () => {
    if (modalState.parameterIndex !== null && modalState.field && tempValue !== null) {
      onParameterChange(modalState.parameterIndex, modalState.field as keyof ExtendedToolParameterSchema, tempValue as PrimitiveValue | SimpleArray);
    }
    closeModal();
  };

  const columnDefinitions = useMemo(() => [
    {
      id: 'internalName',
      header: 'Data Parameter Name',
      width: 150,
      cell: (item: ExtendedToolParameterSchema) => (
        <Box variant="code">{item.internalName}</Box>
      )
    },
    {
      id: 'type',
      header: 'Type',
      width: 120,
      cell: (item: ExtendedToolParameterSchema) => {
        if (item.type === 'array' && item.items?.type) {
          return <Box variant="code">{item.items.type}[]</Box>;
        }
        return <Box variant="code">{item.type}</Box>;
      }
    },
    {
      // for display on AskRino
      id: 'toolParameterName',
      header: 'Tool Parameter Name',
      width: 140,
      cell: (item: ExtendedToolParameterSchema) => {
        const index = parameters.findIndex(p => p === item);
        if (!isEditable) {
          return <Box>{item.parameterName || '-'}</Box>;
        }
        return (
          <Input
            value={item.parameterName || ''}
            onChange={({ detail }) => onParameterChange(index, 'parameterName', detail.value)}
            placeholder="Display name"
          />
        );
      }
    },
    {
      id: 'description',
      header: 'Description',
      minWidth: 400,
      cell: (item: ExtendedToolParameterSchema) => {
        const index = parameters.findIndex(p => p === item);
        return (
          <Textarea
            value={item.description || ''}
            onChange={isEditable ? ({ detail }) => onParameterChange(index, 'description', detail.value) : undefined}
            placeholder={isEditable ? "Parameter description" : undefined}
            readOnly={!isEditable}
            rows={10}
          />
        );
      }
    },
    {
      id: 'required',
      header: 'Required',
      width: 80,
      cell: (item: ExtendedToolParameterSchema) => {
        if (!canEditRequiredField(item.internalName) || !isEditable) {
          return (
            <Checkbox
              checked={!!item.required || isParameterInRequiredList(item.internalName)}
              disabled
              readOnly
            />
          );
        }
        const index = parameters.findIndex(p => p === item);
        return (
          <Checkbox
            checked={!!item.required || isParameterInRequiredList(item.internalName)}
            onChange={({ detail }) => {
              onParameterChange(index, 'required', detail.checked);
            }}
          />
        );
      }
    },
    {
      id: 'default',
      header: 'Default',
      minWidth: 300,
      cell: (item: ExtendedToolParameterSchema) => {
        const index = parameters.findIndex(p => p === item);
        const displayValue = formatDisplayValue(item.default);
        
        return (
          <>
            {isEditable && (
              <Button
                variant="icon"
                iconName="edit"
                onClick={() => openModal(index, 'default', item.default)}
                ariaLabel="Edit default value"
              />
            )}
            <Textarea
              value={displayValue}
              readOnly
              rows={3}
            />
          </>
        );
      }
    },
    {
      id: 'enum',
      header: 'Enum Values',
      minWidth: 300,
      cell: (item: ExtendedToolParameterSchema) => {
        const index = parameters.findIndex(p => p === item);
        const displayValue = item.enum && item.enum.length > 0 ? JSON.stringify(item.enum).replace(/,/g, ', ') : '-';
        
        return (
          <>
            {isEditable && (
              <Button
                variant="icon"
                iconName="edit"
                onClick={() => openModal(index, 'enum', item.enum)}
                ariaLabel="Edit enum values"
              />
            )}
            <Textarea
              value={displayValue}
              readOnly
              rows={3}
            />
          </>
        );
      }
    },
    {
      id: 'examples',
      header: 'Examples',
      minWidth: 300,
      cell: (item: ExtendedToolParameterSchema) => {
        const index = parameters.findIndex(p => p === item);
        const displayValue = formatDisplayValue(item.examples);
        
        return (
          <>
            {isEditable && (
              <Button
                variant="icon"
                iconName="edit"
                onClick={() => openModal(index, 'examples', item.examples)}
                ariaLabel="Edit example value"
              />
            )}
            <Textarea
              value={displayValue}
              readOnly
              rows={3}
            />
          </>
        );
      }
    }
  ], [parameters, onParameterChange, canEditRequiredField, isParameterInRequiredList, openModal, isEditable]);

  const renderModalContent = () => {
    if (!modalState.isOpen || modalState.parameterIndex === null || !modalState.field) {
      return null;
    }

    const parameter = parameters[modalState.parameterIndex];
    const fieldConfig = FIELD_CONFIG[modalState.field];
    const isArrayType = parameter.type === 'array';
    
    // Handle array-only fields (like enum and example)
    if (fieldConfig.isArrayOnly) {
      const currentValues = Array.isArray(tempValue) ? tempValue : [];
      
      // For fields that support nested arrays (like example), show nested editor
      if (fieldConfig.supportsNested) {
        const hasNestedArrays = currentValues.some(val => Array.isArray(val));
        const stateKey = `${modalState.parameterIndex}-${modalState.field}`;
        // Use stored nested mode state if explicitly set, otherwise auto-detect from data
        const isNestedMode = nestedModeStates[stateKey] !== undefined 
          ? nestedModeStates[stateKey] 
          : hasNestedArrays;
        
        return (
          <SpaceBetween direction="vertical" size="m">
            <FormField 
              label={`${fieldConfig.displayName} (Array)`}
              description={isNestedMode ? "Nested list mode: You can create arrays within arrays" : "Simple array mode: Each item is a string value"}
            >
              {isNestedMode ? (
                <NestedListEditor
                  values={currentValues as NestedArray}
                  onChange={(newValues: NestedArray) => {
                    setTempValue(newValues);
                  }}
                  placeholder={fieldConfig.placeholder}
                />
              ) : (
                <ArrayEditor
                  values={currentValues.map(val => Array.isArray(val) ? JSON.stringify(val) : String(val)) as SimpleArray}
                  onChange={(newValues) => {
                    const convertedValues = newValues.map(val => {
                      const strVal = String(val);
                      if (strVal.startsWith('[') && strVal.endsWith(']')) {
                        try {
                          return JSON.parse(strVal);
                        } catch {
                          return val;
                        }
                      }
                      return val;
                    });
                    setTempValue(convertedValues);
                  }}
                  placeholder={fieldConfig.placeholder}
                />
              )}
            </FormField>
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="normal"
                iconName={isNestedMode ? "undo" : "folder"}
                onClick={() => {
                  const stateKey = `${modalState.parameterIndex}-${modalState.field}`;
                  setNestedModeStates(prev => ({
                    ...prev,
                    [stateKey]: !isNestedMode
                  }));
                }}
              >
                {isNestedMode ? 'Switch to simple array' : 'Enable nested lists'}
              </Button>
              {isNestedMode && (
                <Box variant="span" color="text-status-info" fontSize="body-s">
                  💡 Use "Add list" to create nested arrays
                </Box>
              )}
            </SpaceBetween>
          </SpaceBetween>
        );
      } else {
        // Simple array editor for fields like enum
        return (
          <FormField label={fieldConfig.displayName}>
            <ArrayEditor
              values={currentValues.map(val => String(val)) as SimpleArray}
              onChange={(newValues) => setTempValue(newValues)}
              placeholder={fieldConfig.placeholder}
            />
          </FormField>
        );
      }
    }

    // Handle fields that support nested arrays but depend on parameter's isArray setting (default)
    if (fieldConfig.supportsNested) {
      if (isArrayType) {
        const currentValues = Array.isArray(tempValue) ? tempValue : [];
        const hasNestedArrays = currentValues.some(val => Array.isArray(val));
        const stateKey = `${modalState.parameterIndex}-${modalState.field}`;
        // Use stored nested mode state if explicitly set, otherwise auto-detect from data
        const isNestedMode = nestedModeStates[stateKey] !== undefined 
          ? nestedModeStates[stateKey] 
          : hasNestedArrays;
        
        return (
          <SpaceBetween direction="vertical" size="m">
            <FormField 
              label={`${fieldConfig.displayName} (Array)`}
              description={isNestedMode ? "Nested list mode: You can create arrays within arrays" : "Simple array mode: Each item is a string value"}
            >
              {isNestedMode ? (
                <NestedListEditor
                  values={currentValues.map(val => Array.isArray(val) ? val.map(String) : String(val)) as NestedArray}
                  onChange={(newValues: NestedArray) => {
                    setTempValue(newValues);
                  }}
                  placeholder={fieldConfig.placeholder}
                />
              ) : (
                <ArrayEditor
                  values={currentValues.map(val => Array.isArray(val) ? JSON.stringify(val) : String(val)) as SimpleArray}
                  onChange={(newValues) => {
                    const convertedValues = newValues.map(val => {
                      const strVal = String(val);
                      if (strVal.startsWith('[') && strVal.endsWith(']')) {
                        try {
                          return JSON.parse(strVal);
                        } catch {
                          return val;
                        }
                      }
                      return val;
                    });
                    setTempValue(convertedValues);
                  }}
                  placeholder={fieldConfig.placeholder}
                />
              )}
            </FormField>
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="normal"
                iconName={isNestedMode ? "undo" : "folder"}
                onClick={() => {
                  const stateKey = `${modalState.parameterIndex}-${modalState.field}`;
                  setNestedModeStates(prev => ({
                    ...prev,
                    [stateKey]: !isNestedMode
                  }));
                }}
              >
                {isNestedMode ? 'Switch to simple array' : 'Enable nested lists'}
              </Button>
              {isNestedMode && (
                <Box variant="span" color="text-status-info" fontSize="body-s">
                  💡 Use "Add list" to create nested arrays
                </Box>
              )}
            </SpaceBetween>
          </SpaceBetween>
        );
      } else {
        return (
          <FormField label={fieldConfig.displayName}>
            <Input
              value={tempValue !== null && tempValue !== undefined ? String(tempValue) : ''}
              onChange={({ detail }) => setTempValue(detail.value)}
              placeholder={`Enter ${fieldConfig.placeholder}`}
            />
          </FormField>
        );
      }
    }
    
    return null;
  };

  return (
    <>
      <Table
        columnDefinitions={columnDefinitions}
        items={parameters}
        header={
          <Header
            counter={`(${parameters.length})`}
          >
            Tool Parameters
          </Header>
        }
        empty={
          <Box textAlign="center" color="inherit">
            <b>No parameters</b>
            <Box padding={{ bottom: "s" }} variant="p" color="inherit">
              This tool revision has no parameters.
            </Box>
          </Box>
        }
      />
      
      <Modal
        onDismiss={closeModal}
        visible={modalState.isOpen}
        header={modalState.title}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={closeModal}>
                Cancel
              </Button>
              <Button variant="primary" onClick={saveModal}>
                Save
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {renderModalContent()}
      </Modal>
    </>
  );
};

export default ToolParametersTable;
